import { array, compactFlatten } from '@art-suite/art-core-ts';
import { SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH, SCAN_PART_TYPES_BY_DEPTH, ScanPart, ScanRoot, validateScanDataStructureRecursionDepth } from '../ScanRoot';
import { getDeoptimizedScan, getDeoptimizedScanPart } from '../ScanRootOptimized';
import { getScanDataInMemory, getScanDataInMemoryDelta } from './ScanDataInMemoryFunctions';
import { ScanDataInMemory } from './ScanDataInMemoryModels';

const getScanDataInMemoryForScanPartRecursively = (scanId: string, scanPart: ScanPart, recursionDepth: number, now = new Date()): ScanDataInMemory[] => {
  validateScanDataStructureRecursionDepth(recursionDepth);
  const deOptimizedScanPart = getDeoptimizedScanPart(scanPart);
  const {
    id,
    templateId,
    updatedAt,
    createdAt,

    // @ts-ignore - strip all children that may or may not be present
    groups,
    // @ts-ignore - strip all children that may or may not be present
    tasks,
    // @ts-ignore - strip all children that may or may not be present
    options,
    // @ts-ignore - strip all children that may or may not be present
    lineItems,
    // @ts-ignore - strip all children that may or may not be present
    groupTemplates,
    // @ts-ignore - strip all children that may or may not be present
    taskTemplates,
    // @ts-ignore - strip all children that may or may not be present
    optionTemplates,
    // @ts-ignore - strip all children that may or may not be present
    lineItemTemplates,

    // keep all other properties
    ...rest
  } = deOptimizedScanPart;
  const childrenFieldName = SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[recursionDepth];
  const childParts: ScanPart[] | undefined = (scanPart as any)[childrenFieldName] as ScanPart[];

  return compactFlatten([
    getScanDataInMemory({
      scanId,
      scanPartId: id,
      templateScanPartId: templateId,
      type: SCAN_PART_TYPES_BY_DEPTH[recursionDepth],
      data: rest,
      childrenScanPartIds: childParts?.map(childPart => childPart.id),
    }, now),
    ...array(childParts, childPart => getScanDataInMemoryForScanPartRecursively(scanId, childPart, recursionDepth + 1, now)),
  ]);
};

/**
 * Pure function: Get new ScanData records for a new or existing Scan (no database operations)
 * @param scanId
 * @param scanRoot
 * @returns
 */
export const getFullScanDataInMemoryFromScanRoot = (scanId: string, scanRoot: ScanRoot, now = new Date()): ScanDataInMemory[] =>
  getScanDataInMemoryForScanPartRecursively(scanId, getDeoptimizedScan(scanRoot), 0, now);

/**
 * Pure function: Get set of ScanDataInMemory objects representing:
 * - deleted ScanData records
 * - added ScanData records
 * - changed ScanData records
 * - returns all new ScanDataInMemory records - feel free to mutate the updatedAt property
 * comparing before and after ScanRoots
 * @param scanBefore
 * @param scanAfter
 * @returns
 */
export const getScanDataInMemoryDeltaFromScanRoots = (scanId: string, scanBefore: ScanRoot, scanAfter: ScanRoot, now = new Date()): ScanDataInMemory[] =>
  getScanDataInMemoryDelta(
    getFullScanDataInMemoryFromScanRoot(scanId, scanBefore, now),
    getFullScanDataInMemoryFromScanRoot(scanId, scanAfter, now),
  );
